import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';

// ---------------------------------------------------------------------------
// ActivityFeed — a chronological merge of everything that just changed in
// the caseload: students added/updated, IEP goals touched, incidents
// logged. Everything here is real data; the previous version was hardcoded
// mock rows. If a context is empty, it simply contributes nothing.
// ---------------------------------------------------------------------------

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < MS_PER_MINUTE) return 'just now';
  if (diff < MS_PER_HOUR) return `${Math.round(diff / MS_PER_MINUTE)} min ago`;
  if (diff < MS_PER_DAY) return `${Math.round(diff / MS_PER_HOUR)} hr ago`;
  const days = Math.round(diff / MS_PER_DAY);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

const ActivityFeed = () => {
  const navigate = useNavigate();
  const { students } = useStudents();
  const { goals } = useIEPGoals();
  const { incidents } = useIncidents();

  const activities = useMemo(() => {
    const studentsById = new Map(
      (Array.isArray(students) ? students : []).map((s) => [s.id, s]),
    );
    const nameFor = (id) => {
      const s = studentsById.get(id);
      if (!s) return 'Unknown student';
      return (
        `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed student'
      );
    };

    const rows = [];

    for (const s of Array.isArray(students) ? students : []) {
      const ts = s.lastUpdated || s.createdAt;
      if (!ts) continue;
      const isNew =
        s.createdAt && Math.abs(new Date(ts) - new Date(s.createdAt)) < 60000;
      rows.push({
        id: `student-${s.id}-${ts}`,
        type: 'student',
        title: isNew ? 'Student added' : 'Student profile updated',
        studentName:
          `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
          'Unnamed student',
        studentId: s.id,
        timestamp: ts,
        priority: 'low',
      });
    }

    for (const g of Array.isArray(goals) ? goals : []) {
      const ts = g.lastUpdated || g.createdAt;
      if (!ts) continue;
      const mastered = g.status === 'mastered';
      // Prefer the short goal-area label ("reading", "behavior") over the
      // full "specific" text, which can run several sentences.
      const label =
        g.goalArea ||
        (g.specific ? String(g.specific).slice(0, 60) : null);
      rows.push({
        id: `goal-${g.id}-${ts}`,
        type: mastered ? 'goal-mastered' : 'goal',
        title: mastered ? 'Goal mastered' : 'IEP goal updated',
        studentName: nameFor(g.studentId),
        studentId: g.studentId,
        goalId: g.id,
        timestamp: ts,
        priority: mastered ? 'low' : 'medium',
        subtitle: label,
      });
    }

    for (const i of Array.isArray(incidents) ? incidents : []) {
      const ts = i.occurredAt || i.createdAt || i.lastUpdated;
      if (!ts) continue;
      // Incidents use a 1–5 intensity scale (see IncidentEntryForm).
      // 4 or 5 lights up the timeline red so higher-severity events stand
      // out from a run-of-the-mill entry.
      const intensity = Number(i.intensity ?? 0);
      const isHigh = intensity >= 4;
      rows.push({
        id: `incident-${i.id}-${ts}`,
        type: 'incident',
        title: 'Behavior incident logged',
        studentName: nameFor(i.studentId),
        studentId: i.studentId,
        timestamp: ts,
        priority: isHigh ? 'high' : 'medium',
        subtitle: i.behaviorType || null,
      });
    }

    rows.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return rows.slice(0, 6);
  }, [students, goals, incidents]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'student':
        return 'User';
      case 'goal':
        return 'Target';
      case 'goal-mastered':
        return 'CheckCircle2';
      case 'incident':
        return 'AlertTriangle';
      default:
        return 'Activity';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500';
      case 'medium':
        return 'border-l-yellow-500';
      case 'low':
        return 'border-l-green-500';
      default:
        return 'border-l-gray-300';
    }
  };

  const handleActivityClick = (activity) => {
    switch (activity?.type) {
      case 'student':
        if (activity.studentId) {
          navigate(
            `/student-profile-management/${encodeURIComponent(
              activity.studentId,
            )}`,
          );
        } else {
          navigate('/student-profile-management');
        }
        break;
      case 'goal':
      case 'goal-mastered':
        navigate('/iep-goals-and-objectives-tracking');
        break;
      case 'incident':
        navigate('/behavioral-incident-logging');
        break;
      default:
        break;
    }
  };

  const handleViewAll = () => {
    // No single "activity log" page yet; send folks to the incidents page,
    // which is the timestamp-heavy view most teachers reach for.
    navigate('/behavioral-incident-logging');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={14}
          onClick={handleViewAll}
        >
          View All
        </Button>
      </div>

      {activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 py-8 px-4 text-center">
          <Icon name="Sparkles" size={20} className="text-gray-400" />
          <p className="text-sm font-medium text-gray-700">
            Nothing to show yet
          </p>
          <p className="text-xs text-gray-500 max-w-xs">
            Add a student, log an IEP data point, or record an incident and it
            will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <button
              key={activity.id}
              type="button"
              className={`w-full text-left border-l-4 ${getPriorityColor(
                activity.priority,
              )} bg-gray-50 rounded-r-lg p-3 hover:bg-gray-100 cursor-pointer transition-colors duration-200`}
              onClick={() => handleActivityClick(activity)}
            >
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-200 rounded-md flex items-center justify-center mt-0.5 flex-shrink-0">
                  <Icon
                    name={getActivityIcon(activity.type)}
                    size={12}
                    className="text-gray-600"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm mb-1 truncate">
                    {activity.title}
                    {activity.subtitle ? (
                      <span className="text-gray-500 font-normal">
                        {' '}
                        — {activity.subtitle}
                      </span>
                    ) : null}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="truncate">{activity.studentName}</span>
                    <span>•</span>
                    <span>{relativeTime(activity.timestamp)}</span>
                  </div>
                </div>

                <Icon
                  name="ChevronRight"
                  size={14}
                  className="text-gray-400 mt-1 flex-shrink-0"
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
